import { useState, type FormEvent } from "react";
import { useAuth } from "../contexts/AuthContext.js";

/**
 * Login stránka — celostránková (bez TopBaru) v KTM brandingu.
 * Po úspěšném loginu volá `auth.refresh()` přes `AuthProvider`,
 * což přepne `<AuthGate>` do appky.
 */
export function LoginPage(): JSX.Element {
  const { login } = useAuth();
  const [email, setEmail] = useState("");
  const [password, setPassword] = useState("");
  const [error, setError] = useState<string | null>(null);
  const [submitting, setSubmitting] = useState(false);

  async function handleSubmit(e: FormEvent<HTMLFormElement>): Promise<void> {
    e.preventDefault();
    if (!email || !password) return;
    setError(null);
    setSubmitting(true);
    try {
      await login(email, password);
    } catch (err) {
      const code = err instanceof Error ? err.message : "unknown";
      if (code === "invalid_credentials") {
        setError("Neplatné přihlašovací údaje.");
      } else if (code === "missing_credentials") {
        setError("Vyplň jméno i heslo.");
      } else if (code === "feature_disabled") {
        setError("Přihlašování je vypnuté.");
      } else {
        setError(`Chyba přihlášení: ${code}`);
      }
    } finally {
      setSubmitting(false);
    }
  }

  return (
    <div className="min-h-screen bg-gradient-to-br from-zinc-900 via-ktm-black to-zinc-950 flex items-center justify-center px-4">
      <div className="w-full max-w-md">
        {/* Brand */}
        <div className="flex flex-col items-center mb-8">
          <img
            src="/logo.svg"
            alt="KTM Vlašim"
            width={120}
            height={60}
            className="h-14 w-auto select-none"
            draggable={false}
          />
          <h1 className="mt-4 text-white text-xl font-semibold tracking-tight">
            Interní katalog dílů
          </h1>
          <p className="mt-1 text-zinc-400 text-sm">
            Přihlas se pro pokračování
          </p>
        </div>

        {/* Form card */}
        <form
          onSubmit={handleSubmit}
          className="bg-white rounded-xl shadow-2xl p-6 sm:p-8 ring-1 ring-zinc-200"
        >
          <div className="space-y-5">
            <div>
              <label
                htmlFor="email"
                className="block text-xs font-medium text-zinc-700 uppercase tracking-wider mb-1.5"
              >
                Uživatelské jméno
              </label>
              <input
                id="email"
                name="email"
                type="text"
                autoComplete="username"
                required
                autoFocus
                value={email}
                onChange={(e) => setEmail(e.target.value)}
                disabled={submitting}
                className="w-full px-3.5 py-2.5 border border-zinc-300 rounded-lg shadow-sm placeholder-zinc-400 focus:outline-none focus:ring-2 focus:ring-ktm-orange focus:border-transparent text-zinc-900 disabled:bg-zinc-50 disabled:cursor-not-allowed transition"
                placeholder="admin"
              />
            </div>

            <div>
              <label
                htmlFor="password"
                className="block text-xs font-medium text-zinc-700 uppercase tracking-wider mb-1.5"
              >
                Heslo
              </label>
              <input
                id="password"
                name="password"
                type="password"
                autoComplete="current-password"
                required
                value={password}
                onChange={(e) => setPassword(e.target.value)}
                disabled={submitting}
                className="w-full px-3.5 py-2.5 border border-zinc-300 rounded-lg shadow-sm placeholder-zinc-400 focus:outline-none focus:ring-2 focus:ring-ktm-orange focus:border-transparent text-zinc-900 disabled:bg-zinc-50 disabled:cursor-not-allowed transition"
                placeholder="••••••••"
              />
            </div>

            {error && (
              <div
                role="alert"
                className="rounded-lg bg-red-50 border border-red-200 px-3.5 py-2.5 text-sm text-red-700"
              >
                {error}
              </div>
            )}

            <button
              type="submit"
              disabled={submitting || !email || !password}
              className="w-full bg-ktm-orange hover:bg-orange-600 disabled:bg-zinc-300 disabled:cursor-not-allowed text-white font-semibold py-2.5 rounded-lg shadow-sm transition focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-ktm-orange"
            >
              {submitting ? "Přihlašuji..." : "Přihlásit se"}
            </button>
          </div>
        </form>

        <p className="mt-6 text-center text-xs text-zinc-500">
          KTM Vlašim · Interní katalog
        </p>
      </div>
    </div>
  );
}
